// WebView 환경 감지 유틸리티

/**
 * WebView 환경을 감지하는 함수
 * 범용적인 감지 방법을 우선 사용하고, 특정 앱 패턴은 보조적으로 사용
 */
export const detectWebView = () => {
  const ua = navigator.userAgent || '';
  
  // 1. Android WebView 범용 감지
  // Android WebView는 "wv" 토큰이나 Chrome 없이 Version 문자열이 있는 경우
  const isAndroidWebView = (
    /Android/i.test(ua) && (
      // 표준 WebView 마커 "wv"
      /wv/i.test(ua) || 
      // Chrome이 아닌데 Version 문자열이 있는 경우
      (/Version\/[\d.]+/i.test(ua) && !/Chrome\/[\d.]+/i.test(ua)) ||
      // Android 앱 내장 브라우저 패턴
      /Android.*AppleWebKit(?!.*Chrome)/i.test(ua)
    )
  );
  
  // 2. iOS WebView 범용 감지
  // iOS WebView는 Safari, Chrome, Firefox, Opera 브라우저가 아닌 경우
  const isIOSWebView = (
    /(iPhone|iPod|iPad)/i.test(ua) && 
    /AppleWebKit/i.test(ua) && 
    // 알려진 iOS 브라우저들이 아닌 경우
    !/(Safari|CriOS|FxiOS|OPiOS|EdgiOS)/i.test(ua)
  );
  
  // 3. Feature Detection을 통한 추가 검증
  const hasWebViewFeatures = (): boolean => {
    try {
      // iOS WebView는 특정 JavaScript 기능이 다르게 동작
      if (/iP(hone|od|ad)/i.test(navigator.platform || '')) {
        // iOS WebView에서는 window.indexedDB가 존재하지만 Safari와 다르게 동작
        // standalone 모드가 아니면서 Safari가 아닌 경우
        const standalone = (window.navigator as any).standalone;
        if (standalone === false) {
          return false; // Safari browser
        }
        if (standalone === true) {
          return false; // PWA mode
        }
        // standalone이 undefined이고 Safari 표시가 없으면 WebView
        return !/(Safari|CriOS|FxiOS|OPiOS)/i.test(ua);
      }
      
      // Android에서 추가 체크
      if (/Android/i.test(ua)) {
        // Android WebView는 특정 window 속성이 있을 수 있음
        return (window as any).Android !== undefined || 
               (window as any).webkit?.messageHandlers !== undefined;
      }
    } catch (e) {
      // Feature detection 실패 시 무시
    }
    return false;
  };
  
  // 4. 특정 앱 패턴 감지 (선택적 - 앱별 맞춤 메시지용)
  const appPatterns = {
    // 한국 주요 앱들
    ssodam: /Ssodam/i,
    kakao: /KAKAOTALK/i,
    naver: /NAVER\(inapp/i,
    line: /Line\//i,
    // 글로벌 소셜 앱들
    facebook: /FB(AN|AV)/i,
    instagram: /Instagram/i,
    twitter: /Twitter/i,
    linkedin: /LinkedInApp/i,
    snapchat: /Snapchat/i,
    tiktok: /TikTok/i,
    // 일반 InApp 브라우저 표시
    inapp: /InApp|IAB/i,
    webview: /WebView/i
  };
  
  // 앱 타입 감지 (있으면)
  let appType: string | null = null;
  for (const [key, pattern] of Object.entries(appPatterns)) {
    if (pattern.test(ua)) {
      appType = key;
      break;
    }
  }
  
  // 최종 WebView 판정
  const isWebView = isAndroidWebView || isIOSWebView || hasWebViewFeatures();
  
  // 감지 방법 기록 (디버깅용)
  let detectionMethod: string | null = null;
  if (isAndroidWebView) {
    if (/wv/i.test(ua)) {
      detectionMethod = 'android-wv-token';
    } else if (/Version\/[\d.]+/i.test(ua) && !/Chrome\/[\d.]+/i.test(ua)) {
      detectionMethod = 'android-version-without-chrome';
    } else {
      detectionMethod = 'android-webkit-pattern';
    }
  } else if (isIOSWebView) {
    detectionMethod = 'ios-no-browser-signature';
  } else if (hasWebViewFeatures()) {
    detectionMethod = 'feature-detection';
  }
  
  return {
    isWebView,
    webViewType: isWebView ? (appType || 'generic') : null,
    isIOS: /(iPhone|iPod|iPad)/i.test(ua),
    isAndroid: /Android/i.test(ua),
    detectionMethod,
    userAgent: ua
  };
};

/**
 * 외부 브라우저에서 URL을 여는 함수
 */
export const openInExternalBrowser = (url: string = window.location.href) => {
  const { isAndroid, isIOS } = detectWebView();
  
  if (isAndroid) {
    // Android Intent 방식 시도
    // Chrome 우선, 실패 시 기본 브라우저
    const cleanUrl = url.replace(/^https?:\/\//, '');
    const intents = [
      `intent://${cleanUrl}#Intent;scheme=https;package=com.android.chrome;end`,
      `intent://${cleanUrl}#Intent;scheme=https;action=android.intent.action.VIEW;end`
    ];
    
    // 첫 번째 Intent 시도
    window.location.href = intents[0];
    
    // 실패 시 대체 방법
    setTimeout(() => {
      // Intent가 실패했다면 두 번째 시도
      window.location.href = intents[1];
    }, 100);
    
  } else if (isIOS) {
    // iOS는 직접 열기가 제한적이므로 클립보드 복사 후 안내
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(url)
        .then(() => {
          alert('주소가 복사되었습니다.\nSafari를 열고 주소창에 붙여넣기 해주세요.');
        })
        .catch(() => {
          // 클립보드 API 실패 시 수동 복사 안내
          prompt('아래 주소를 복사하여 Safari에서 열어주세요:', url);
        });
    } else {
      // 클립보드 API를 사용할 수 없는 경우
      prompt('아래 주소를 복사하여 Safari에서 열어주세요:', url);
    }
    
  } else {
    // 데스크톱이나 기타 환경에서는 window.open 시도
    const newWindow = window.open(url, '_blank');
    if (!newWindow) {
      // 팝업 차단 등으로 실패한 경우
      alert(`브라우저에서 직접 열어주세요:\n${url}`);
    }
  }
};

/**
 * WebView별 맞춤 메시지를 반환하는 함수
 */
export const getWebViewMessage = (webViewType: string | null): string => {
  // 특정 앱이 감지된 경우 맞춤 메시지
  const appMessages: Record<string, string> = {
    ssodam: '서담(Ssodam) 앱 내부 브라우저에서 오류가 발생했습니다.',
    kakao: '카카오톡 내부 브라우저에서 오류가 발생했습니다.',
    naver: '네이버 앱 브라우저에서 오류가 발생했습니다.',
    line: '라인 앱 브라우저에서 오류가 발생했습니다.',
    facebook: '페이스북 앱 브라우저에서 오류가 발생했습니다.',
    instagram: '인스타그램 앱 브라우저에서 오류가 발생했습니다.',
    twitter: '트위터 앱 브라우저에서 오류가 발생했습니다.',
    linkedin: '링크드인 앱 브라우저에서 오류가 발생했습니다.',
    snapchat: '스냅챗 앱 브라우저에서 오류가 발생했습니다.',
    tiktok: '틱톡 앱 브라우저에서 오류가 발생했습니다.',
    inapp: '앱 내부 브라우저에서 오류가 발생했습니다.',
    webview: '앱 내부 브라우저에서 오류가 발생했습니다.',
    generic: '앱 내부 브라우저에서 오류가 발생했습니다.'
  };
  
  if (webViewType && appMessages[webViewType]) {
    return appMessages[webViewType];
  }
  
  // 기본 메시지
  return '앱 내부 브라우저에서 오류가 발생했습니다.';
};